import styled, { keyframes } from 'styled-components';

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0.9);
    opacity: 0;
  }
  to {
    transform: scale(1);
    opacity: 1;
  }
`;

export const ModalWrapper = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  animation: ${fadeIn} 0.3s ease-out;
`;

export const ModalContent = styled.div<{ $hasImage?: boolean }>`
  background: white;
  border-radius: 2rem;
  max-width: 90vw;
  width: 370px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.25);
  position: relative;
  display: flex;
  flex-direction: column;
  animation: ${scaleIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: #f1f5f9; }
  &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

  @media (min-width: 1024px) {
    width: ${props => props.$hasImage ? '820px' : '560px'};
    max-width: 92vw;
    max-height: 88vh;
    display: ${props => props.$hasImage ? 'grid' : 'flex'};
    grid-template-columns: ${props => props.$hasImage ? '340px 1fr' : 'none'};
    overflow: hidden;
    flex-direction: column;
  }
`;

export const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;

  @media (min-width: 1024px) {
    grid-column: 1;
    grid-row: 1;
    background: #f3f4f6;
    border-radius: 2rem 0 0 2rem;
    padding: 0;
    overflow: hidden;
    min-height: 480px;
  }
`;

export const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #374151;
  z-index: 10;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 2px 8px rgba(0,0,0,0.12);

  &:hover {
    transform: scale(1.1);
    background: white;
  }

  &:active {
    transform: scale(0.95);
  }
`;

export const ProductImage = styled.img`
  max-width: 100%;
  max-height: 40vh;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
  border-radius: 1rem;

  @media (min-width: 1024px) {
    max-height: none;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
  }
`;

export const ImagePlaceholder = styled.div`
  width: 300px;
  height: 300px;
  max-height: 40vh;
  border-radius: 1rem;
  background: #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;

  i { font-size: 5rem; }

  @media (min-width: 1024px) {
    width: 100%;
    height: 100%;
    max-height: none;
    border-radius: 0;
  }
`;

export const ContentWrapper = styled.div<{ $fullWidth?: boolean }>`
  padding: 0 1.5rem 1.5rem;

  @media (min-width: 1024px) {
    grid-column: ${props => props.$fullWidth ? '1 / -1' : '2'};
    grid-row: 1;
    overflow-y: auto;
    padding: 2rem 2rem 2rem 1.75rem;
    display: flex;
    flex-direction: column;

    &::-webkit-scrollbar { width: 6px; }
    &::-webkit-scrollbar-track { background: #f1f5f9; }
    &::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
  }
`;

export const OptionsScrollArea = styled.div`
  @media (min-width: 1024px) {
    flex: 1;
    overflow-y: auto;
    max-height: 240px;
    margin: 0 -4px;
    padding: 0 4px;

    &::-webkit-scrollbar { width: 4px; }
    &::-webkit-scrollbar-track { background: transparent; }
    &::-webkit-scrollbar-thumb { background: #d1d5db; border-radius: 4px; }
  }
`;

export const ProductInfo = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

export const ProductName = styled.h2`
  font-size: 1.5rem;
  font-weight: 900;
  color: #1f2937;
  margin: 0 0 12px 0;
  text-transform: uppercase;
`;

export const ProductDescription = styled.p`
  font-size: 0.95rem;
  color: #6b7280;
  line-height: 1.6;
  margin: 0 0 16px 0;
`;

export const ProductPrice = styled.div`
  font-size: 1.5rem;
  font-weight: 900;
  color: var(--secondary, #d32f2f);
  margin-bottom: 24px;
`;

export const OptionsSection = styled.div`
  margin-bottom: 24px;
`;

export const OptionLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 700;
  color: #6b7280;
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

export const ChipsContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-height: 150px;
  overflow-y: auto;
  padding: 4px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
`;

export const Chip = styled.button<{ $active: boolean }>`
  padding: 10px 20px;
  border-radius: 12px;
  border: 2px solid ${props => props.$active ? 'var(--primary, #f58220)' : '#e2e8f0'};
  background: ${props => props.$active ? 'var(--primary, #f58220)' : 'white'};
  color: ${props => props.$active ? 'white' : '#6b7280'};
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    transform: ${props => props.$active ? 'scale(1.05)' : 'scale(1.02)'};
  }

  &:active {
    transform: scale(0.98);
  }
`;

export const HintText = styled.p`
  font-size: 0.7rem;
  color: #9ca3af;
  margin-top: 8px;
  text-align: left;
`;

export const OptionTable = styled.div`
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
`;

export const OptionRow = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 11px 12px;
  background: ${props => props.$active ? 'rgba(245,130,32,0.06)' : 'white'};
  border: none;
  border-bottom: 1px dashed #e2e8f0;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${props => props.$active ? 'rgba(245,130,32,0.10)' : '#fafafa'};
  }

  &:active {
    transform: scale(0.99);
  }
`;

export const OptionCheckbox = styled.div<{ $active: boolean }>`
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 2px solid ${props => props.$active ? 'var(--primary, #f58220)' : '#cbd5e1'};
  background: ${props => props.$active ? 'var(--primary, #f58220)' : 'white'};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
`;

export const OptionRowInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const OptionRowName = styled.div<{ $active: boolean }>`
  font-size: 0.875rem;
  font-weight: ${props => props.$active ? '700' : '500'};
  color: ${props => props.$active ? '#1f2937' : '#374151'};
  line-height: 1.3;
`;

export const OptionRowDesc = styled.div`
  font-size: 0.72rem;
  color: #9ca3af;
  margin-top: 2px;
  line-height: 1.4;
`;

export const OptionRowPrice = styled.div`
  font-size: 0.82rem;
  font-weight: 700;
  color: #10b981;
  white-space: nowrap;
  flex-shrink: 0;
`;

export const AddButton = styled.button<{ $storeOpen: boolean }>`
  width: 100%;
  background: ${props => props.$storeOpen ? 'var(--primary, #f58220)' : '#d1d5db'};
  color: ${props => props.$storeOpen ? 'white' : '#6b7280'};
  font-weight: 900;
  font-size: 1.1rem;
  padding: 16px 32px;
  border-radius: 16px;
  border: none;
  cursor: ${props => props.$storeOpen ? 'pointer' : 'not-allowed'};
  transition: all 0.2s;
  box-shadow: ${props => props.$storeOpen ? '0 4px 12px rgba(245, 130, 32, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)'};
  text-transform: uppercase;
  pointer-events: ${props => props.$storeOpen ? 'auto' : 'none'};
  opacity: 1;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(245, 130, 32, 0.4);
    filter: brightness(1.1);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    transform: none;
  }
`;
